import { useState } from 'react';
import { Folder } from 'lucide-react';
import { useFoldersStore } from '@/store/foldersStore';
import { useAuthStore } from '@/store/authStore';
import { useAccounts } from '@/store/accountsStore';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';

interface AddFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AddFolderModal({ isOpen, onClose }: AddFolderModalProps) {
  const { user } = useAuthStore();
  const { accounts = [] } = useAccounts();
  const { addFolder } = useFoldersStore();
  const [folderName, setFolderName] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Ensure accounts is an array before sorting
  const sortedAccounts = Array.isArray(accounts) 
    ? [...accounts].sort((a, b) => 
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      )
    : [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !folderName.trim()) return;

    setIsSubmitting(true);
    try {
      await addFolder({
        name: folderName.trim(),
        user_id: user.id,
        account_ids: selectedAccounts,
        color: '#439476', // Default color
        icon: 'folder', // Default icon
        display_order: 0 // Default order
      });

      toast.success('Folder created successfully');
      
      setFolderName('');
      setSelectedAccounts([]);
      onClose();
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Failed to create folder');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] p-0 bg-white">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle className="text-2xl font-semibold flex items-center gap-2">
            <Folder className="w-6 h-6" />
            Create New Folder
          </DialogTitle>
          <DialogDescription className="text-base">
            Create a new folder to organize your accounts. You can select multiple accounts to include in this folder.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="px-6 pb-6">
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="folderName" className="text-sm font-medium">
                Folder Name
              </Label>
              <Input
                id="folderName"
                placeholder="Enter folder name"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                className="h-11"
                required
              />
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Select Accounts</Label>
              <Card className="border rounded-lg p-1">
                <ScrollArea className="h-[200px] pr-4">
                  <div className="space-y-1 p-3">
                    {sortedAccounts.length > 0 ? (
                      sortedAccounts.map((account) => (
                        <div
                          key={account.id}
                          className="flex items-center space-x-3 rounded-md p-2 hover:bg-muted transition-colors"
                        >
                          <Checkbox
                            id={account.id}
                            checked={selectedAccounts.includes(account.id)}
                            onCheckedChange={(checked) => {
                              setSelectedAccounts(prev =>
                                checked
                                  ? [...prev, account.id]
                                  : prev.filter(id => id !== account.id)
                              );
                            }}
                            className="h-5 w-5"
                          />
                          <Label
                            htmlFor={account.id}
                            className="cursor-pointer flex-grow text-sm"
                          >
                            {account.name}
                          </Label>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        No accounts available. Please create an account first.
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </Card>
            </div>
          </div>

          <div className="flex justify-end space-x-4 mt-8">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="h-11"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !folderName.trim() || sortedAccounts.length === 0}
              className="h-11 gap-2"
            >
              <Folder className="w-4 h-4" />
              Create Folder
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
} 